<template>
  <transition name="fade">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/40 backdrop-blur-sm"
      @click.self="cancel"
    >
      <div
        class="bg-white rounded-2xl shadow-xl max-w-xl w-full flex flex-col overflow-hidden border border-gray-100 rise-in"
      >
        <!-- Modal header -->
        <div
          class="px-6 py-4 border-b border-gray-100 flex items-center justify-between"
        >
          <h3 class="text-lg font-bold text-ink flex items-center gap-2">
            <font-awesome-icon icon="info-circle" class="text-brand-600" />
            <span>Confirm Data Correction</span>
          </h3>
          <button
            class="text-ink-muted hover:text-ink hover:bg-gray-100 w-8 h-8 rounded-full flex items-center justify-center focus:outline-none transition-colors"
            @click="cancel"
          >
            <font-awesome-icon icon="times" />
          </button>
        </div>

        <!-- Modal body -->
        <div class="p-6 overflow-y-auto max-h-[60vh] space-y-5">
          <p class="text-sm text-ink-soft">
            You are correcting data. Please review the changes below to confirm.
          </p>

          <!-- Changes List -->
          <div
            class="border border-gray-100 rounded-xl overflow-hidden bg-gray-50/50"
          >
            <div
              class="grid grid-cols-3 gap-2 px-4 py-2 bg-gray-100 text-xs font-bold text-ink-muted uppercase tracking-wider"
            >
              <span>Field</span>
              <span>Original Value</span>
              <span>Corrected Value</span>
            </div>
            <div class="divide-y divide-gray-100">
              <div
                v-for="change in changes"
                :key="change.key"
                class="grid grid-cols-3 gap-2 px-4 py-3 text-sm items-center"
              >
                <span
                  class="font-semibold text-ink truncate"
                  :title="change.label"
                  >{{ change.label }}</span
                >
                <span
                  class="text-red-500 line-through truncate"
                  :title="formatValue(change.oldValue)"
                >
                  {{ formatValue(change.oldValue) }}
                </span>
                <span
                  class="text-green-600 font-medium truncate"
                  :title="formatValue(change.newValue)"
                >
                  {{ formatValue(change.newValue) }}
                </span>
              </div>
            </div>
          </div>

          <!-- Reason for change (audit trail). Only shown when the caller needs
               it; the value is emitted with the confirm event. -->
          <div v-if="requireReason">
            <label
              for="correction-reason"
              class="block text-sm font-semibold text-ink mb-1.5"
            >
              Reason for this correction
              <span class="text-red-500">*</span>
            </label>
            <textarea
              id="correction-reason"
              v-model="reason"
              rows="3"
              placeholder="e.g. Marked complete in error — reopening for missing visit-2 data."
              class="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/40 focus:border-brand-500 resize-y"
            ></textarea>
            <p class="text-xs text-ink-muted mt-1">
              Recorded in the audit log alongside this change.
            </p>
          </div>
        </div>

        <!-- Modal footer -->
        <div
          class="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 bg-gray-50/50"
        >
          <button class="btn btn-secondary btn-md" @click="cancel">
            Cancel
          </button>
          <button
            class="btn btn-primary btn-md flex items-center gap-1.5"
            :disabled="requireReason && !reason.trim()"
            :class="{
              'opacity-50 cursor-not-allowed': requireReason && !reason.trim(),
            }"
            @click="confirm"
          >
            <span>Confirm Changes</span>
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'ConfirmChangesModal',
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    changes: {
      type: Array,
      required: true,
    },
    // When true, a mandatory free-text reason is collected and emitted with the
    // confirm event (needed by audited corrections, e.g. correct_patient). When
    // false the modal behaves as before and emits confirm with no argument.
    requireReason: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      reason: '',
    }
  },
  watch: {
    // Clear any previous reason each time the modal reopens.
    isOpen(open) {
      if (open) this.reason = ''
    },
  },
  methods: {
    formatValue(val) {
      if (val === null || val === undefined) return '(empty)'
      if (typeof val === 'boolean') return val ? 'Yes' : 'No'
      if (typeof val === 'object') return JSON.stringify(val)
      return String(val)
    },
    cancel() {
      this.$emit('cancel')
    },
    confirm() {
      if (this.requireReason) {
        const reason = this.reason.trim()
        if (!reason) return
        this.$emit('confirm', reason)
      } else {
        this.$emit('confirm')
      }
    },
  },
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
.rise-in {
  animation: riseIn 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}
@keyframes riseIn {
  from {
    transform: translateY(12px) scale(0.98);
    opacity: 0;
  }
  to {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
}
</style>
